<app-page size="narrow">
    <div class="crumb-row">
        <app-breadcrumbs [crumbs]="crumbs ?? []"></app-breadcrumbs>
        @if (form()?.get('id').value && (Features.CompaniesDetailDelete | hasFeature)) {
            <button (click)="openDelete($event)" class="delete-button" mat-stroked-button type="button">
                <i class="fa-regular fa-trash-can"></i> Bedrijf verwijderen
            </button>
        }
    </div>

    @if (form()) {
        <div [formGroup]="form()" class="sections">
            <app-section description="Naam en contactgegevens, gebruikt in overzichten en op facturen." icon="building" title="Algemeen">
                <app-field for="name" label="Naam">
                    <mat-form-field>
                        <input formControlName="name" id="name" matInput name="name" placeholder="Bijv. Track Line B.V.">
                        <mat-hint>Officiële bedrijfsnaam.</mat-hint>
                        <mat-error [control]="form().get('name')"></mat-error>
                    </mat-form-field>
                </app-field>
                <app-field for="short" label="Afkorting" optional>
                    <mat-form-field>
                        <input formControlName="short" id="short" matInput name="short" placeholder="Bijv. TL">
                        <mat-hint>Korte naam in krappe overzichten.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="color" label="Kleur" optional>
                    <mat-form-field>
                        <span [style.background]="isHexColor(form().value.color) ? form().value.color : null" class="color-swatch" matPrefix></span>
                        <input class="font-monospace" formControlName="color" id="color" matInput name="color" placeholder="#1e8e5a">
                        <mat-hint>Hex-code, bijv. #1e8e5a. Kleurt de klant in Daily, Weekly en Urenregistratie.</mat-hint>
                        <mat-error [control]="form().get('color')"></mat-error>
                    </mat-form-field>
                </app-field>
                <app-field for="email" label="E-mailadres" optional>
                    <mat-form-field>
                        <input formControlName="email" id="email" matInput name="email" placeholder="administratie@bedrijf.nl">
                        <mat-hint>Hierheen gaan facturen.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field for="humres" label="Ter attentie van" optional>
                    <mat-form-field>
                        <input formControlName="humres" id="humres" matInput name="humres" placeholder="Bijv. Crediteurenadministratie">
                        <mat-hint>Contactpersoon of afdeling op post en facturen.</mat-hint>
                    </mat-form-field>
                </app-field>
            </app-section>

            <app-section [columns]="10" class="address" description="Postadres voor briefpapier en facturen." icon="location-dot" title="Adres">
                <app-field class="span-6" for="street" label="Straat">
                    <mat-form-field>
                        <input formControlName="street" id="street" matInput name="street" placeholder="Leerparkpromenade">
                    </mat-form-field>
                </app-field>
                <app-field class="span-2" for="number" label="Nummer">
                    <mat-form-field>
                        <input formControlName="number" id="number" matInput name="number" placeholder="40" type="number">
                    </mat-form-field>
                </app-field>
                <app-field class="span-2" for="addition" label="Toevoeging">
                    <mat-form-field>
                        <input formControlName="addition" id="addition" matInput name="addition" placeholder="A">
                    </mat-form-field>
                </app-field>
                <app-field class="span-3" for="postal_code" label="Postcode">
                    <mat-form-field>
                        <input class="font-monospace" formControlName="postal_code" id="postal_code" matInput name="postal_code" placeholder="3312 KW">
                    </mat-form-field>
                </app-field>
                <app-field class="span-7" for="city" label="Plaats">
                    <mat-form-field>
                        <input formControlName="city" id="city" matInput name="city" placeholder="Dordrecht">
                    </mat-form-field>
                </app-field>
                @if (addressLines.length && (form().value.street || form().value.postal_code || form().value.city)) {
                    <div class="ui-info-block address-preview">
                        <span class="preview-label">Zo staat het op de factuur</span>
                        @for (line of addressLines; track $index) {
                            <span>{{ line }}</span>
                        }
                    </div>
                }
            </app-section>

            <app-section description="Koppelt het bedrijf aan een project en team, zodat taken en pull requests in Daily en Weekly bij deze klant komen."
                         icon="list-check"
                         title="Azure DevOps">
                <app-field class="readonly guid" for="azure_project_id" label="Project-id" optional>
                    <mat-form-field>
                        <input class="font-monospace" formControlName="azure_project_id" id="azure_project_id" matInput name="azure_project_id"
                               placeholder="00000000-0000-0000-0000-000000000000" readonly>
                        <mat-hint>GUID uit Azure DevOps → Project settings.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field class="readonly guid" for="azure_team_id" label="Team-id" optional>
                    <mat-form-field>
                        <input class="font-monospace" formControlName="azure_team_id" id="azure_team_id" matInput name="azure_team_id"
                               placeholder="00000000-0000-0000-0000-000000000000" readonly>
                        <mat-hint>Team binnen het project.</mat-hint>
                    </mat-form-field>
                </app-field>
                <app-field class="readonly" for="azure_desc" full label="Weergavenaam" optional>
                    <mat-form-field>
                        <input formControlName="azure_desc" id="azure_desc" matInput name="azure_desc" placeholder="Bijv. Track Line Team / Track Line" readonly>
                        <mat-hint>Getoond in het bedrijvenoverzicht (veld "Beschrijving").</mat-hint>
                    </mat-form-field>
                </app-field>

                <!-- Not in the design: the existing Azure tools to inspect items and (re)connect a team -->
                @if (form()?.get('id').value && (Features.CompaniesAzureitems | hasFeature) && (Features.CompaniesAzureteams | hasFeature)) {
                    <div class="azure full-row">
                        <div class="azure-heading">
                            <span class="azure-title">Azure integration</span>
                            @if (company?.azure_desc) {
                                <span class="ui-help">
                                    This company is currently connected to {{company.azure_desc}}.
                                    Use "Load Azure teams" to connect to another team.
                                </span>
                            }
                        </div>

                        <div>
                            <button (click)="loadAzureItems()" mat-stroked-button type="button">
                                Load Azure items
                            </button>
                        </div>
                        @if (azureItems()?.length) {
                            <pre class="json">{{azureItems() | json}}</pre>
                        }

                        <div>
                            <button (click)="loadAzureTeams()" mat-stroked-button type="button">
                                Load Azure teams
                            </button>
                        </div>
                        @if (azureTeams()?.length) {
                            <span class="azure-title">Choose an Azure team:</span>
                            <div class="d-flex flex-wrap gap-2">
                                @for (team of azureTeams(); track team.id) {
                                    <button (click)="connectAzure(team)" mat-stroked-button type="button">
                                        {{team.name}} / {{team.projectName}}
                                    </button>
                                }
                            </div>
                        }
                    </div>
                }
            </app-section>

            <!-- TODO(design): README open point 13 — a debtor shared by several companies and the meaning of Groundbirds / Intern are not confirmed; the explanations below are the design's. -->
            <app-section description="Koppeling met AFAS en onder welk label het bedrijf valt." icon="euro-sign" title="Administratie">
                <app-field class="debtor" full label="AFAS-debiteur" optional>
                    <ng-select [items]="debtors()" [searchFn]="searchDebtor" [searchable]="true" bindLabel="debtorName" bindValue="debtorId"
                               formControlName="afas_debtor_id" placeholder="Geen koppeling">
                        <ng-template let-item="item" ng-label-tmp><span class="font-monospace">{{ item.debtorId }}</span> · {{ item.debtorName }}</ng-template>
                        <ng-template let-item="item" ng-option-tmp><span class="font-monospace">{{ item.debtorId }}</span> · {{ item.debtorName }}</ng-template>
                    </ng-select>
                    <span hint>Naar deze debiteur gaan facturen en afrekeningen.</span>
                </app-field>
                <div class="flags full-row">
                    <mat-checkbox class="check-card" formControlName="is_groundbirds" name="is_groundbirds">
                        <span class="check-title">Groundbirds</span>
                        <span class="check-description">Bedrijf valt onder het label Groundbirds.</span>
                    </mat-checkbox>
                    <mat-checkbox class="check-card" formControlName="is_internal" name="is_internal">
                        <span class="check-title">Intern</span>
                        <span class="check-description">Eigen organisatie; uren tellen niet als klantwerk.</span>
                    </mat-checkbox>
                </div>
            </app-section>
        </div>

        @if (Features.CompaniesSave | hasFeature) {
            <app-sticky-save-bar
                (cancel)="cancel()"
                (save)="save()"
                [dirty]="form().dirty"
                [saveLabel]="form().get('id').value ? 'Opslaan' : 'Bedrijf aanmaken'"
                [valid]="form().valid">
            </app-sticky-save-bar>
        }
    }
</app-page>
